import React, { useState, useEffect, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, TrendingUp } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useCatalogData } from '../hooks/useCatalogData';

// Memoized countdown timer component - prevents re-rendering of entire Home page every second
const DealCountdown = memo(() => {
  const [timeLeft, setTimeLeft] = useState({
    days: 2,
    hours: 23,
    minutes: 59,
    seconds: 58
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { days, hours, minutes, seconds } = prev;

        if (seconds > 0) {
          seconds--;
        } else {
          seconds = 59;
          if (minutes > 0) {
            minutes--;
          } else {
            minutes = 59;
            if (hours > 0) {
              hours--;
            } else {
              hours = 23;
              if (days > 0) {
                days--;
              }
            }
          }
        }

        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex justify-center space-x-4 mb-8">
      <div className="bg-white rounded-lg p-4 min-w-[80px] shadow">
        <div className="text-3xl font-bold text-red-600">{String(timeLeft.days).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Days</div>
      </div>
      <div className="bg-white rounded-lg p-4 min-w-[80px] shadow">
        <div className="text-3xl font-bold text-red-600">{String(timeLeft.hours).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Hours</div>
      </div>
      <div className="bg-white rounded-lg p-4 min-w-[80px] shadow">
        <div className="text-3xl font-bold text-red-600">{String(timeLeft.minutes).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Mins</div>
      </div>
      <div className="bg-white rounded-lg p-4 min-w-[80px] shadow">
        <div className="text-3xl font-bold text-red-600">{String(timeLeft.seconds).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Secs</div>
      </div>
    </div>
  );
});

DealCountdown.displayName = 'DealCountdown';

const Home = () => {
  const {
    products: catalogProducts = [],
    categories: catalogCategories = [],
    brands: catalogBrands = [],
    loading,
    error,
  } = useCatalogData();
  const [selectedTab, setSelectedTab] = useState('all');

  const products = useMemo(() => catalogProducts ?? [], [catalogProducts]);
  const categories = useMemo(() => catalogCategories ?? [], [catalogCategories]);
  const brandOptions = useMemo(
    () =>
      (catalogBrands ?? [])
        .map((brand) => ({
          value: brand?.id ?? brand?.slug ?? brand?.name ?? '',
          label: brand?.name ?? brand?.slug ?? '',
        }))
        .filter((brand) => brand.value && brand.label),
    [catalogBrands],
  );

  const blogPosts = useMemo(() => [], []);

  const featuredProducts = useMemo(() => products.slice(0, 8), [products]);
  const dealProducts = useMemo(
    () => products.filter((p) => Array.isArray(p.badges) && p.badges.includes('sale')).slice(0, 8),
    [products],
  );
  const newArrivals = useMemo(
    () => products.filter((p) => Array.isArray(p.badges) && p.badges.includes('new')).slice(0, 12),
    [products],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Vehicle Finder */}
      <section className="bg-gradient-to-r from-gray-100 to-gray-200 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Find Parts For Your Equipment</h1>
            <p className="text-lg text-gray-600">Over hundreds of brands and tens of thousands of parts</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <select className="px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-red-500">
                <option>Select Year</option>
                {[...Array(17)].map((_, i) => (
                  <option key={i} value={2008 + i}>{2008 + i}</option>
                ))}
              </select>
              <select className="px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-red-500">
                <option>Select Brand</option>
                {brandOptions.map((brand) => (
                  <option key={brand.value} value={brand.value}>
                    {brand.label}
                  </option>
                ))}
              </select>
              <select className="px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-red-500">
                <option>Select Model</option>
                <option>Model A</option>
                <option>Model B</option>
                <option>Model C</option>
              </select>
              <select className="px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-red-500">
                <option>Select Type</option>
                <option>Type 1</option>
                <option>Type 2</option>
                <option>Type 3</option>
              </select>
              <button className="bg-red-600 text-white px-6 py-3 rounded hover:bg-red-700 transition font-semibold">
                Search
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="text-red-600 text-4xl mb-2">📦</div>
              <h3 className="font-bold text-gray-900 mb-1">Free Shipping</h3>
              <p className="text-sm text-gray-600">For orders from $50</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-red-600 text-4xl mb-2">💬</div>
              <h3 className="font-bold text-gray-900 mb-1">Support 24/7</h3>
              <p className="text-sm text-gray-600">Call us anytime</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-red-600 text-4xl mb-2">🔒</div>
              <h3 className="font-bold text-gray-900 mb-1">100% Safety</h3>
              <p className="text-sm text-gray-600">Only secure payments</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-red-600 text-4xl mb-2">🔥</div>
              <h3 className="font-bold text-gray-900 mb-1">Hot Offers</h3>
              <p className="text-sm text-gray-600">Discounts up to 70%</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Featured Products</h2>
            <div className="flex space-x-4">
              <button
                onClick={() => setSelectedTab('all')}
                className={`px-4 py-2 rounded transition ${selectedTab === 'all' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedTab('power-tools')}
                className={`px-4 py-2 rounded transition ${selectedTab === 'power-tools' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Power Tools
              </button>
              <button
                onClick={() => setSelectedTab('hand-tools')}
                className={`px-4 py-2 rounded transition ${selectedTab === 'hand-tools' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Hand Tools
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(loading && featuredProducts.length === 0 ? Array.from({ length: 8 }) : featuredProducts)
              .filter((product) => {
                if (!product) return true;
                return selectedTab === 'all' || product.category === selectedTab;
              })
              .slice(0, 8)
              .map((product, index) =>
                product ? (
                  <ProductCard key={product.id} product={product} />
                ) : (
                  <div
                    key={`placeholder-${index}`}
                    className="h-72 bg-white border border-gray-200 rounded-lg animate-pulse"
                  />
                ),
              )}
          </div>
          {error && (
            <div className="mt-6 text-center text-sm text-gray-500">
              We had trouble loading live product data. Showing fallback content.
            </div>
          )}
        </div>
      </section>

      {/* Deal Zone */}
      <section className="py-12 bg-gradient-to-r from-red-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 bg-red-600 text-white px-6 py-2 rounded-full mb-4">
              <TrendingUp size={20} />
              <span className="font-bold">Attention! Deal Zone</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Hurry up! Discounts up to 70%</h2>
            
            {/* Countdown */}
            <DealCountdown />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {dealProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Shop by Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                className="relative group overflow-hidden rounded-lg shadow-lg h-64"
              >
                <img
                  src={category.image || '/placeholder.jpg'}
                  alt={category.name}
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = '/placeholder.jpg';
                  }}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{category.name}</h3>
                  <ul className="text-sm space-y-1 mb-4">
                    {(category.subcategories ?? []).slice(0, 3).map((sub, idx) => (
                      <li key={idx}>• {sub}</li>
                    ))}
                  </ul>
                  <div className="flex items-center text-sm font-semibold">
                    <span>Shop All</span>
                    <ChevronRight size={16} className="ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">New Arrivals</h2>
            <Link to="/products" className="text-red-600 hover:text-red-700 font-semibold flex items-center">
              View All <ChevronRight size={20} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newArrivals.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="py-12 bg-white border-t">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Popular Brands</h2>
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-6">
            {brandOptions.map((brand) => (
              <Link
                key={brand.value}
                to={`/products?brand=${brand.value}`}
                className="flex items-center justify-center p-4 border border-gray-200 rounded hover:border-red-600 hover:shadow-md transition bg-white"
              >
                <span className="font-semibold text-gray-700 hover:text-red-600">{brand.label}</span>
              </Link>
            ))}
          </div>
          {brandOptions.length === 0 && (
            <div className="text-center text-sm text-gray-500">No brands available at the moment.</div>
          )}
        </div>
      </section>

      {/* Blog */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Latest News</h2>
          {blogPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="bg-white rounded-lg overflow-hidden shadow hover:shadow-lg transition"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="text-sm text-red-600 font-semibold mb-2">{post.category}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-red-600 transition">
                      {post.title}
                    </h3>
                    <div className="text-sm text-gray-500 mb-3">
                      By {post.author} on {post.date}
                    </div>
                    <p className="text-gray-600 text-sm">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center text-sm text-gray-500">
              Blog content will be available soon. Check back later for updates.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
